import { EnvironmentDetailsContentHeader } from "@/components/environments/EnvironmentDetailsContentHeader";
import { EnvironmentDetailsHeader } from "@/components/environments/EnvironmentDetailsHeader";
import { EnvironmentDeletionWarning } from "@/components/environments/EnvironmentDeletionWarning";
import { EnvironmentOnboarding } from "@/components/environments/EnvironmentOnboarding";
import { EnvironmentPorts } from "@/components/environments/EnvironmentPorts";
import { EnvironmentSecretsModal } from "@/components/environments/EnvironmentSecretsModal";
import { EnvironmentStartSequence } from "@/components/environments/EnvironmentStartSequence";
import { cn } from "@/components/podkit/lib/cn";
import { useEnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import { useFlagOnaAIEnabled, useFlagOnaEnvironmentOnboardingEnabled } from "@/hooks/use-flag";
import { TrackLocations } from "@/hooks/use-segment";
import { useEnvironment } from "@/queries/environment-queries";
import { EnvironmentAutomations } from "@/routes/environments/details/EnvironmentAutomations";
import { GitStatusPanel } from "@/routes/environments/details/GitStatusPanel";
import { useState, type FC, type PropsWithChildren } from "react";

export type EnvironmentDetailsProps = {
    environmentId: string;
};

export const EnvironmentDetails: FC<EnvironmentDetailsProps> = ({ environmentId }) => {
    const [showSecretsModal, setShowSecretsModal] = useState(false);
    const { value: isOnaEnvironmentOnboardingEnabled } = useFlagOnaEnvironmentOnboardingEnabled();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();
    const { showingAgentConversation } = useEnvironmentDetailsOutletContext();

    return (
        <div
            className={cn("flex min-h-full flex-col gap-6 @container", isOnaEnabled && "h-full")}
            data-testid="environment-details"
        >
            {!showingAgentConversation && <EnvironmentDetailsHeader showActionBar={true} />}
            {showingAgentConversation && <EnvironmentDetailsContentHeader environmentId={environmentId} />}

            <WhenReady environmentId={environmentId}>
                <>
                    <EnvironmentDeletionWarning environmentId={environmentId} />
                    <EnvironmentStartSequence
                        environmentId={environmentId}
                        onShowSecrets={() => setShowSecretsModal(true)}
                    />
                    <GitStatusPanel environmentId={environmentId} />
                    {!isOnaEnvironmentOnboardingEnabled && (
                        <EnvironmentOnboarding
                            environmentId={environmentId}
                            data-track-location={TrackLocations.EnvironmentOnboarding}
                        />
                    )}
                    <EnvironmentPorts environmentId={environmentId} />
                    <EnvironmentAutomations environmentId={environmentId} />
                </>
            </WhenReady>

            {showSecretsModal && (
                <EnvironmentSecretsModal onClose={() => setShowSecretsModal(false)} environmentId={environmentId} />
            )}
        </div>
    );
};

export const WhenReady: FC<PropsWithChildren & { environmentId: string }> = ({ children, environmentId }) => {
    const { data: environment, isPending } = useEnvironment(environmentId);
    const ready = !isPending && !!environment;

    if (!ready) {
        return null;
    }

    return children;
};
